import { DB_ONLY_CLICK_ID_KEYS, RAW_UTM_KEYS } from './mixpanelTypes'
import {
  MIXPANEL_OAUTH_ATTRIBUTION_STORAGE_KEY,
  safeRemove
} from './mixpanelStorage'
import type {
  MixpanelAdditionalInfo,
  OAuthMixpanelEventProperties
} from './mixpanelTypes'
import type { MixpanelStorage } from './mixpanelStorage'

const MIXPANEL_OAUTH_ATTRIBUTION_KEY = MIXPANEL_OAUTH_ATTRIBUTION_STORAGE_KEY
const MIXPANEL_OAUTH_ATTRIBUTION_TTL_MS = 30 * 60 * 1000

interface OAuthAttributionSnapshot {
  mixpanelAdditionalInfo: MixpanelAdditionalInfo
  oauthEventProperties: OAuthMixpanelEventProperties
  createdAt: number
}

export type OAuthMixpanelTrackingSnapshot = Pick<
  OAuthAttributionSnapshot,
  'mixpanelAdditionalInfo' | 'oauthEventProperties'
>

interface GetOAuthMixpanelTrackingSnapshotOptions {
  storage?: MixpanelStorage | null
  now?: number
  ttlMs?: number
  fallback: () => Promise<MixpanelAdditionalInfo>
}

function getBrowserSessionStorage(): MixpanelStorage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function pickOAuthAttributionInfo(
  info: MixpanelAdditionalInfo
): MixpanelAdditionalInfo {
  const picked: MixpanelAdditionalInfo = {
    context_id: info.context_id,
    session_source: info.session_source,
    session_medium: info.session_medium,
    device_category: info.device_category,
    location: info.location
  }

  for (const key of [...RAW_UTM_KEYS, ...DB_ONLY_CLICK_ID_KEYS]) {
    const value = info[key]
    if (typeof value === 'string') picked[key] = value
  }

  return picked
}

function pickOAuthEventProperties(
  properties: OAuthMixpanelEventProperties
): OAuthMixpanelEventProperties {
  const currentUrl = properties.$current_url
  if (
    typeof currentUrl !== 'string' ||
    currentUrl.trim().length === 0 ||
    currentUrl.length > 2_048
  ) {
    return {}
  }
  return { $current_url: currentUrl }
}

function parseMixpanelAdditionalInfo(
  value: unknown
): MixpanelAdditionalInfo | undefined {
  if (!isRecord(value)) return undefined
  if (
    typeof value.context_id !== 'string' ||
    typeof value.session_source !== 'string' ||
    typeof value.session_medium !== 'string' ||
    typeof value.location !== 'string' ||
    (value.device_category !== 'desktop' &&
      value.device_category !== 'mobile' &&
      value.device_category !== 'tablet')
  ) {
    return undefined
  }

  const info: MixpanelAdditionalInfo = {
    context_id: value.context_id,
    session_source: value.session_source,
    session_medium: value.session_medium,
    device_category: value.device_category,
    location: value.location
  }
  for (const key of [...RAW_UTM_KEYS, ...DB_ONLY_CLICK_ID_KEYS]) {
    const field = value[key]
    if (typeof field === 'string') info[key] = field
  }
  return info
}

function parseOAuthAttributionSnapshot(
  value: string
): OAuthAttributionSnapshot | undefined {
  try {
    const parsed: unknown = JSON.parse(value)
    if (!isRecord(parsed) || typeof parsed.createdAt !== 'number') {
      return undefined
    }
    const mixpanelAdditionalInfo = parseMixpanelAdditionalInfo(
      parsed.mixpanelAdditionalInfo
    )
    if (!mixpanelAdditionalInfo) return undefined
    const rawOAuthEventProperties = isRecord(parsed.oauthEventProperties)
      ? parsed.oauthEventProperties
      : {}
    return {
      mixpanelAdditionalInfo,
      oauthEventProperties: pickOAuthEventProperties({
        $current_url:
          typeof rawOAuthEventProperties.$current_url === 'string'
            ? rawOAuthEventProperties.$current_url
            : undefined
      }),
      createdAt: parsed.createdAt
    }
  } catch {
    return undefined
  }
}

export function saveOAuthAttributionSnapshot(
  info: MixpanelAdditionalInfo,
  eventProperties: OAuthMixpanelEventProperties,
  storage: MixpanelStorage | null = getBrowserSessionStorage(),
  now = Date.now()
): void {
  if (!storage) return
  try {
    storage.setItem(
      MIXPANEL_OAUTH_ATTRIBUTION_KEY,
      JSON.stringify({
        mixpanelAdditionalInfo: pickOAuthAttributionInfo(info),
        oauthEventProperties: pickOAuthEventProperties(eventProperties),
        createdAt: now
      } satisfies OAuthAttributionSnapshot)
    )
  } catch {
    return
  }
}

export function clearOAuthAttributionSnapshot(
  storage: MixpanelStorage | null = getBrowserSessionStorage()
): void {
  safeRemove(storage, MIXPANEL_OAUTH_ATTRIBUTION_KEY)
}

export function getOAuthAttributionSnapshot(
  storage: MixpanelStorage | null = getBrowserSessionStorage(),
  now = Date.now(),
  ttlMs = MIXPANEL_OAUTH_ATTRIBUTION_TTL_MS
): OAuthMixpanelTrackingSnapshot | undefined {
  if (!storage) return undefined
  try {
    const value = storage.getItem(MIXPANEL_OAUTH_ATTRIBUTION_KEY)
    if (!value) return undefined
    const snapshot = parseOAuthAttributionSnapshot(value)
    if (!snapshot || now - snapshot.createdAt > ttlMs) {
      safeRemove(storage, MIXPANEL_OAUTH_ATTRIBUTION_KEY)
      return undefined
    }
    return {
      mixpanelAdditionalInfo: snapshot.mixpanelAdditionalInfo,
      oauthEventProperties: snapshot.oauthEventProperties
    }
  } catch {
    return undefined
  }
}

export async function getOAuthMixpanelTrackingSnapshot({
  storage = getBrowserSessionStorage(),
  now = Date.now(),
  ttlMs = MIXPANEL_OAUTH_ATTRIBUTION_TTL_MS,
  fallback
}: GetOAuthMixpanelTrackingSnapshotOptions): Promise<OAuthMixpanelTrackingSnapshot> {
  return (
    getOAuthAttributionSnapshot(storage, now, ttlMs) ?? {
      mixpanelAdditionalInfo: await fallback(),
      oauthEventProperties: {}
    }
  )
}
